import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _classPrivateFieldLooseBase from '@babel/runtime/helpers/esm/classPrivateFieldLooseBase';
import _classPrivateFieldLooseKey from '@babel/runtime/helpers/esm/classPrivateFieldLooseKey';
import { exposeGlobally, normalizeFlags, normalizeFlag } from '@flopflip/adapter-utilities';
import createCache from '@flopflip/localstorage-cache';
import { AdapterSubscriptionStatus, AdapterConfigurationStatus, adapterIdentifiers, AdapterInitializationStatus } from '@flopflip/types';
import isEqual from 'lodash/isEqual';
import mitt from 'mitt';
import warning from 'tiny-warning';

const intialAdapterState = {
  subscriptionStatus: AdapterSubscriptionStatus.Subscribed,
  configurationStatus: AdapterConfigurationStatus.Unconfigured,
  flags: {},
  lockedFlags: new Set(),
  user: {},
  emitter: mitt()
};
const STORAGE_SLICE = '@flopflip';
var _adapterState = /*#__PURE__*/_classPrivateFieldLooseKey("adapterState");
var _internalConfiguredStatusChange__ = /*#__PURE__*/_classPrivateFieldLooseKey("__internalConfiguredStatusChange__");
var _cache = /*#__PURE__*/_classPrivateFieldLooseKey("cache");
var _getIsAdapterUnsubscribed = /*#__PURE__*/_classPrivateFieldLooseKey("getIsAdapterUnsubscribed");
var _getIsFlagLocked = /*#__PURE__*/_classPrivateFieldLooseKey("getIsFlagLocked");
var _didFlagsChange = /*#__PURE__*/_classPrivateFieldLooseKey("didFlagsChange");
var _getFlagsCacheKey = /*#__PURE__*/_classPrivateFieldLooseKey("getFlagsCacheKey");
var _subscribeToFlagsChanges = /*#__PURE__*/_classPrivateFieldLooseKey("subscribeToFlagsChanges");
class LocalStorageAdapter {
  constructor() {
    this.id = void 0;
    Object.defineProperty(this, _adapterState, {
      writable: true,
      value: void 0
    });
    // eslint-disable-next-line @typescript-eslint/prefer-readonly
    Object.defineProperty(this, _internalConfiguredStatusChange__, {
      writable: true,
      value: '__internalConfiguredStatusChange__'
    });
    Object.defineProperty(this, _cache, {
      writable: true,
      value: createCache({
        prefix: STORAGE_SLICE
      })
    });
    Object.defineProperty(this, _getIsAdapterUnsubscribed, {
      writable: true,
      value: () => _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].subscriptionStatus === AdapterSubscriptionStatus.Unsubscribed
    });
    Object.defineProperty(this, _getIsFlagLocked, {
      writable: true,
      value: flagName => _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].lockedFlags.has(flagName)
    });
    Object.defineProperty(this, _didFlagsChange, {
      writable: true,
      value: nextFlags => {
        const previousFlags = _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].flags;
        if (previousFlags === undefined) return true;
        return !isEqual(nextFlags, previousFlags);
      }
    });
    Object.defineProperty(this, _getFlagsCacheKey, {
      writable: true,
      value: user => [user.key, 'flags'].filter(Boolean).join('/')
    });
    Object.defineProperty(this, _subscribeToFlagsChanges, {
      writable: true,
      value: _ref => {
        let _ref$pollingIntervalM = _ref.pollingIntervalMs,
          pollingIntervalMs = _ref$pollingIntervalM === void 0 ? 1000 * 60 : _ref$pollingIntervalM,
          user = _ref.user;
        setInterval(() => {
          if (!_classPrivateFieldLooseBase(this, _getIsAdapterUnsubscribed)[_getIsAdapterUnsubscribed]()) {
            const nextFlags = normalizeFlags(_classPrivateFieldLooseBase(this, _cache)[_cache].get(_classPrivateFieldLooseBase(this, _getFlagsCacheKey)[_getFlagsCacheKey](user)));
            if (_classPrivateFieldLooseBase(this, _didFlagsChange)[_didFlagsChange](nextFlags)) {
              _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].flags = nextFlags;
              _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.emit('flagsStateChange', nextFlags);
            }
          }
        }, pollingIntervalMs);
      }
    });
    this.updateFlags = (flags, options) => {
      const isAdapterConfigured = this.getIsConfigurationStatus(AdapterConfigurationStatus.Configured);
      warning(isAdapterConfigured, '@flopflip/localstorage-adapter: adapter not configured. Flags can not be updated before.');
      if (!isAdapterConfigured) return;
      const flagsCacheKey = _classPrivateFieldLooseBase(this, _getFlagsCacheKey)[_getFlagsCacheKey](_classPrivateFieldLooseBase(this, _adapterState)[_adapterState].user);
      const previousFlags = _classPrivateFieldLooseBase(this, _cache)[_cache].get(flagsCacheKey);
      const updatedFlags = Object.entries(flags).reduce((updatedFlags, _ref2) => {
        let _ref3 = _slicedToArray(_ref2, 2),
          flagName = _ref3[0],
          flagValue = _ref3[1];
        const _normalizeFlag = normalizeFlag(flagName, flagValue),
          _normalizeFlag2 = _slicedToArray(_normalizeFlag, 2),
          normalizedFlagName = _normalizeFlag2[0],
          normalizedFlagValue = _normalizeFlag2[1];
        if (_classPrivateFieldLooseBase(this, _getIsFlagLocked)[_getIsFlagLocked](normalizedFlagName)) {
          return updatedFlags;
        }
        if (options !== null && options !== void 0 && options.lockFlags) {
          _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].lockedFlags.add(normalizedFlagName);
        }
        updatedFlags = _objectSpread(_objectSpread({}, updatedFlags), {}, {
          [normalizedFlagName]: normalizedFlagValue
        });
        return updatedFlags;
      }, {});
      const nextFlags = _objectSpread(_objectSpread({}, previousFlags), updatedFlags);
      _classPrivateFieldLooseBase(this, _cache)[_cache].set(flagsCacheKey, nextFlags);
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].flags = nextFlags;
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.emit('flagsStateChange', nextFlags);
    };
    this.unsubscribe = () => {
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].subscriptionStatus = AdapterSubscriptionStatus.Unsubscribed;
    };
    this.subscribe = () => {
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].subscriptionStatus = AdapterSubscriptionStatus.Subscribed;
    };
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState] = _objectSpread({}, intialAdapterState);
    this.id = adapterIdentifiers.localstorage;
  }
  async configure(adapterArgs, adapterEventHandlers) {
    const handleFlagsChange = nextFlags => {
      if (_classPrivateFieldLooseBase(this, _getIsAdapterUnsubscribed)[_getIsAdapterUnsubscribed]()) return;
      adapterEventHandlers.onFlagsStateChange({
        flags: nextFlags,
        id: this.id
      });
    };
    const handleStatusChange = nextStatus => {
      if (_classPrivateFieldLooseBase(this, _getIsAdapterUnsubscribed)[_getIsAdapterUnsubscribed]()) return;
      adapterEventHandlers.onStatusStateChange({
        status: nextStatus,
        id: this.id
      });
    };
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.on('flagsStateChange', handleFlagsChange);
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.on('statusStateChange', handleStatusChange);
    this.setConfigurationStatus(AdapterConfigurationStatus.Configuring);
    const user = adapterArgs.user,
      pollingIntervalMs = adapterArgs.pollingIntervalMs;
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].user = user;
    return Promise.resolve().then(() => {
      this.setConfigurationStatus(AdapterConfigurationStatus.Configured);
      const flags = normalizeFlags(_classPrivateFieldLooseBase(this, _cache)[_cache].get(_classPrivateFieldLooseBase(this, _getFlagsCacheKey)[_getFlagsCacheKey](user)));
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].flags = flags;
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.emit('flagsStateChange', flags);
      _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.emit(_classPrivateFieldLooseBase(this, _internalConfiguredStatusChange__)[_internalConfiguredStatusChange__]);
      _classPrivateFieldLooseBase(this, _subscribeToFlagsChanges)[_subscribeToFlagsChanges]({
        pollingIntervalMs,
        user
      });
      return {
        initializationStatus: AdapterInitializationStatus.Succeeded
      };
    });
  }
  async reconfigure(adapterArgs, _adapterEventHandlers) {
    const previousFlags = _classPrivateFieldLooseBase(this, _cache)[_cache].get(_classPrivateFieldLooseBase(this, _getFlagsCacheKey)[_getFlagsCacheKey](adapterArgs.user));
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].flags = previousFlags || {};
    const nextUser = adapterArgs.user;
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].user = nextUser;
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.emit('flagsStateChange', {});
    return Promise.resolve({
      initializationStatus: AdapterInitializationStatus.Succeeded
    });
  }
  async waitUntilConfigured() {
    return new Promise(resolve => {
      if (this.getIsConfigurationStatus(AdapterConfigurationStatus.Configured)) resolve();else _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.on(_classPrivateFieldLooseBase(this, _internalConfiguredStatusChange__)[_internalConfiguredStatusChange__], resolve);
    });
  }
  getIsConfigurationStatus(configurationStatus) {
    return _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].configurationStatus === configurationStatus;
  }
  setConfigurationStatus(nextConfigurationStatus) {
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].configurationStatus = nextConfigurationStatus;
    _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].emitter.emit('statusStateChange', {
      configurationStatus: _classPrivateFieldLooseBase(this, _adapterState)[_adapterState].configurationStatus
    });
  }
}
const adapter = new LocalStorageAdapter();
exposeGlobally(adapter);

const version = "13.1.7";

export { STORAGE_SLICE, adapter as default, version };
